import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface Task {
  id: number;
  user_id?: number;
  prompt: string;
  agent_count: number;
  status: string;
  created_at: string;
  completed_at?: string;
}

interface MongoResearchData {
  finaloutput?: {
    research_summary?: string;
    sources?: Array<{
      url: string;
      page_title: string;
      relevance_score: number;
      type?: string;
      summary?: string;
    }>;
    product_recommendations?: Array<{
      name: string;
      description: string;
      source_url: string;
      recommendation_reason: string;
      price_range: string;
      availability: string;
      image_url?: string;
    }>;
    insights_considerations?: string[];
    media_opportunities?: Array<{
      product_name: string;
      media_channels?: Array<{
        channel: string;
        opportunity_type: string;
        description: string;
        target_audience: string;
        expected_reach: string;
        implementation_tips: string;
      }>;
      brand_visibility_strategies?: string[];
      conversion_tactics?: string[];
    }>;
  };
  research_summary?: string;
  product_recommendations?: Array<{
    name: string;
    description: string;
    source_url: string;
    recommendation_reason: string;
    price_range: string;
    availability: string;
    image_url?: string;
  }>;
  insights_considerations?: string[];
  media_opportunities?: Array<{
    product_name: string;
    media_channels?: Array<{
      channel: string;
      opportunity_type: string;
      description: string;
      target_audience: string;
      expected_reach: string;
      implementation_tips: string;
    }>;
    brand_visibility_strategies?: string[];
    conversion_tactics?: string[];
  }>;
  searchqueriesused?: string[];
}

const stripMarkdown = (text: string): string => {
  return text
    .replace(/#{1,6}\s+/g, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const splitTextToLines = (text: string, maxWidth: number, doc: jsPDF): string[] => {
  const lines = doc.splitTextToSize(text, maxWidth);
  return lines;
};

export const exportTaskToPDF = (task: Task, mongoResearchData: MongoResearchData | null): void => {
  if (!mongoResearchData) {
    return;
  }

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - 2 * margin;
  let yPosition = margin;

  const addNewPageIfNeeded = (requiredHeight: number): void => {
    if (yPosition + requiredHeight > pageHeight - margin) {
      doc.addPage();
      yPosition = margin;
    }
  };

  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Task Research Report', margin, yPosition);
  yPosition += 10;

  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(`Task ID: ${task.id}`, margin, yPosition);
  yPosition += 7;

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  doc.text(`Created: ${formatDate(task.created_at)}`, margin, yPosition);
  yPosition += 7;

  if (task.completed_at) {
    doc.text(`Completed: ${formatDate(task.completed_at)}`, margin, yPosition);
    yPosition += 7;
  }

  doc.text(`Status: ${task.status.replace('_', ' ').toUpperCase()}`, margin, yPosition);
  yPosition += 10;

  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  yPosition += 5;
  addNewPageIfNeeded(20);
  doc.text('Task Prompt', margin, yPosition);
  yPosition += 8;

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  const promptLines = splitTextToLines(task.prompt, contentWidth, doc);
  doc.text(promptLines, margin, yPosition);
  yPosition += promptLines.length * 6 + 10;

  const researchSummary = mongoResearchData.finaloutput?.research_summary || mongoResearchData.research_summary;
  if (researchSummary) {
    addNewPageIfNeeded(30);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Research Summary', margin, yPosition);
    yPosition += 8;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    const summaryText = stripMarkdown(researchSummary);
    const summaryLines = splitTextToLines(summaryText, contentWidth, doc);
    doc.text(summaryLines, margin, yPosition);
    yPosition += summaryLines.length * 6 + 10;
  }

  const productRecommendations = mongoResearchData.finaloutput?.product_recommendations || mongoResearchData.product_recommendations || [];
  if (productRecommendations.length > 0) {
    addNewPageIfNeeded(40);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Product Recommendations', margin, yPosition);
    yPosition += 10;

    productRecommendations.forEach((product, index) => {
      addNewPageIfNeeded(50);
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(`${index + 1}. ${product.name}`, margin, yPosition);
      yPosition += 7;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      
      if (product.description) {
        const descText = stripMarkdown(product.description);
        const descLines = splitTextToLines(descText, contentWidth, doc);
        doc.text(descLines, margin, yPosition);
        yPosition += descLines.length * 5 + 5;
      }

      if (product.recommendation_reason) {
        doc.setFont('helvetica', 'bold');
        doc.text('Why Recommended:', margin, yPosition);
        yPosition += 6;
        doc.setFont('helvetica', 'normal');
        const reasonText = stripMarkdown(product.recommendation_reason);
        const reasonLines = splitTextToLines(reasonText, contentWidth, doc);
        doc.text(reasonLines, margin, yPosition);
        yPosition += reasonLines.length * 5 + 5;
      }

      doc.text(`Price Range: ${product.price_range}`, margin, yPosition);
      yPosition += 6;
      doc.text(`Availability: ${product.availability}`, margin, yPosition);
      yPosition += 10;
    });
  }

  const insights = mongoResearchData.finaloutput?.insights_considerations || mongoResearchData.insights_considerations || [];
  if (insights.length > 0) {
    addNewPageIfNeeded(30);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Insights & Considerations', margin, yPosition);
    yPosition += 10;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    
    insights.forEach((insight, index) => {
      addNewPageIfNeeded(30);
      const insightText = stripMarkdown(insight);
      const insightLines = splitTextToLines(`${index + 1}. ${insightText}`, contentWidth, doc);
      doc.text(insightLines, margin, yPosition);
      yPosition += insightLines.length * 6 + 8;
    });
  }

  const mediaOpportunities = mongoResearchData.finaloutput?.media_opportunities || mongoResearchData.media_opportunities || [];
  if (mediaOpportunities.length > 0) {
    addNewPageIfNeeded(40);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Media Opportunities', margin, yPosition);
    yPosition += 10;

    mediaOpportunities.forEach((opportunity, index) => {
      addNewPageIfNeeded(50);
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(`${index + 1}. ${opportunity.product_name}`, margin, yPosition);
      yPosition += 8;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');

      if (opportunity.media_channels && opportunity.media_channels.length > 0) {
        doc.setFont('helvetica', 'bold');
        doc.text('Media Channels:', margin, yPosition);
        yPosition += 7;
        doc.setFont('helvetica', 'normal');

        opportunity.media_channels.forEach((channel) => {
          addNewPageIfNeeded(40);
          const channelText = `${channel.channel} (${channel.opportunity_type}): ${channel.description}`;
          const channelLines = splitTextToLines(channelText, contentWidth, doc);
          doc.text(channelLines, margin, yPosition);
          yPosition += channelLines.length * 5 + 5;
          doc.text(`Target Audience: ${channel.target_audience}`, margin, yPosition);
          yPosition += 5;
          doc.text(`Expected Reach: ${channel.expected_reach}`, margin, yPosition);
          yPosition += 8;
        });
      }

      if (opportunity.brand_visibility_strategies && opportunity.brand_visibility_strategies.length > 0) {
        addNewPageIfNeeded(30);
        doc.setFont('helvetica', 'bold');
        doc.text('Brand Visibility Strategies:', margin, yPosition);
        yPosition += 7;
        doc.setFont('helvetica', 'normal');

        opportunity.brand_visibility_strategies.forEach((strategy) => {
          const strategyLines = splitTextToLines(`• ${strategy}`, contentWidth, doc);
          doc.text(strategyLines, margin, yPosition);
          yPosition += strategyLines.length * 5 + 4;
        });
        yPosition += 5;
      }

      if (opportunity.conversion_tactics && opportunity.conversion_tactics.length > 0) {
        addNewPageIfNeeded(30);
        doc.setFont('helvetica', 'bold');
        doc.text('Conversion Tactics:', margin, yPosition);
        yPosition += 7;
        doc.setFont('helvetica', 'normal');

        opportunity.conversion_tactics.forEach((tactic) => {
          const tacticLines = splitTextToLines(`• ${tactic}`, contentWidth, doc);
          doc.text(tacticLines, margin, yPosition);
          yPosition += tacticLines.length * 5 + 4;
        });
        yPosition += 5;
      }

      yPosition += 5;
    });
  }

  const sources = mongoResearchData.finaloutput?.sources || [];
  if (sources.length > 0) {
    addNewPageIfNeeded(50);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Research Sources', margin, yPosition);
    yPosition += 10;

    const sortedSources = [...sources].sort((a, b) => b.relevance_score - a.relevance_score);

    autoTable(doc, {
      startY: yPosition,
      head: [['Title', 'Type', 'Relevance', 'URL']],
      body: sortedSources.map(source => [
        source.page_title || '',
        source.type || 'N/A',
        `${Math.round(source.relevance_score * 100)}%`,
        source.url || ''
      ]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [66, 139, 202], textColor: 255 },
      margin: { left: margin, right: margin },
    });

    yPosition = (doc as any).lastAutoTable.finalY + 10;
  }

  const searchQueries = mongoResearchData.searchqueriesused || [];
  if (searchQueries.length > 0) {
    addNewPageIfNeeded(40);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Search Queries Used', margin, yPosition);
    yPosition += 10;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    searchQueries.forEach((query, index) => {
      addNewPageIfNeeded(15);
      doc.text(`${index + 1}. ${query}`, margin, yPosition);
      yPosition += 7;
    });
  }

  addNewPageIfNeeded(30);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(128, 128, 128);
  const disclaimer = 'This research was conducted by artificial intelligence and may contain inaccuracies or outdated information. Please verify important findings and consult additional sources before making decisions based on these results.';
  const disclaimerLines = splitTextToLines(disclaimer, contentWidth, doc);
  doc.text(disclaimerLines, margin, yPosition);

  const fileName = `task-${task.id}-report-${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
};

